// =============================================================
// COMPONENTS — BTR Companion (Premium dark UI)
// =============================================================
import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { champSquare, itemImg as ddItemImg, getSpell, spellImgByKey, allChamps, ddVersion } from './ddragon';
import { REGION_CODES, matchesQuery } from './format';

// ---------- Icons ----------
function Icon({ name, size = 16, ...rest }) {
  const p = ICON[name]; if (!p) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
         fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" {...rest}>
      {p}
    </svg>
  );
}
const ICON = {
  search: <><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></>,
  arrowR: <><path d="M5 12h14"/><path d="M13 5l7 7-7 7"/></>,
  arrowL: <><path d="M19 12H5"/><path d="M11 5l-7 7 7 7"/></>,
  arrowUR: <path d="M7 17L17 7M8 7h9v9"/>,
  chevD: <path d="M6 9l6 6 6-6"/>,
  chevR: <path d="M9 18l6-6-6-6"/>,
  chevL: <path d="M15 18l-6-6 6-6"/>,
  shuffle: <><path d="M16 3h5v5M4 20l17-17M21 16v5h-5M15 15l6 6M4 4l5 5"/></>,
  plus: <><path d="M12 5v14M5 12h14"/></>,
  check: <path d="M5 13l4 4L19 7"/>,
  x: <><path d="M18 6L6 18M6 6l12 12"/></>,
  star: <path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.9L12 17.8 5.8 21l1.2-6.9-5-4.9 6.9-1L12 2z"/>,
  starFill: <path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.9L12 17.8 5.8 21l1.2-6.9-5-4.9 6.9-1L12 2z" fill="currentColor"/>,
  bookmark: <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2v16z"/>,
  share: <><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4"/></>,
  sun: <><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></>,
  moon: <path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z"/>,
  refresh: <><path d="M21 12a9 9 0 11-3-6.7L21 8M21 3v5h-5"/></>,
  copy: <><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></>,
  filter: <path d="M22 3H2l8 9.5V19l4 2v-8.5L22 3z"/>,
  trendUp: <path d="M3 17l6-6 4 4 8-8M14 7h7v7"/>,
  trendDown: <path d="M3 7l6 6 4-4 8 8M14 17h7v-7"/>,
  bell: <><path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.7 21a2 2 0 01-3.4 0"/></>,
  trophy: <><path d="M6 9H4a2 2 0 010-4h2M18 9h2a2 2 0 000-4h-2M6 5h12v7a6 6 0 01-12 0V5zM12 18v4M8 22h8"/></>,
  swords: <><path d="M14.5 17.5L3 6V3h3l11.5 11.5M13 19l6-6M16 16l4 4M19 21l2-2M3 21l9-9"/></>,
  play: <path d="M6 4v16l14-8L6 4z" fill="currentColor"/>,
  flame: <path d="M8.5 14.5A2.5 2.5 0 0011 17c1.7 0 3-1.4 3-3 0-1-.5-1.8-1-2.5-1.2-1.5-3-2.5-3-5C7 8 5 11 5 13a7 7 0 1014 0c0-3.4-3.5-7.4-5-9-.5 5-3 6.5-5.5 10.5z"/>,
  layers: <><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5M2 12l10 5 10-5"/></>,
  sparkles: <><path d="M12 3v18M3 12h18M5 5l14 14M19 5L5 19"/></>,
  dot: <circle cx="12" cy="12" r="4" fill="currentColor"/>,
  globe: <><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15 15 0 010 20M12 2a15 15 0 000 20"/></>,
  clock: <><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></>,
  user: <><circle cx="12" cy="8" r="4"/><path d="M4 21v-2a6 6 0 016-6h4a6 6 0 016 6v2"/></>,
  menu: <><path d="M4 7h16M4 12h16M4 17h16"/></>,
  download: <><path d="M12 3v12M6 9l6 6 6-6"/><path d="M4 21h16"/></>,
  alert: <><path d="M12 9v4M12 17h.01"/><path d="M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z"/></>,
};

// ---------- BTR Logo ----------
function BTR({ size = 18 }) {
  return (
    <span style={{
      fontFamily: "var(--font-display)",
      fontWeight: 800,
      fontSize: size,
      letterSpacing: "-0.025em",
      color: "var(--ink)",
      display: "inline-flex",
      alignItems: "baseline",
      gap: 1,
    }}>
      btr<span style={{color:"var(--accent)"}}>.</span>
    </span>
  );
}

// ---------- Top Nav ----------
const NAV_TABS = [
  { id: "home",      label: "Accueil" },
  { id: "profile",   label: "Profil" },
  { id: "champions", label: "Champions" },
  { id: "build",     label: "Builds" },
  { id: "status",    label: "Serveurs" },
  { id: "guesser",   label: "Jeux" },
  { id: "arena",     label: "Arena" },
  { id: "clash",     label: "Clash" },
];

function NavBar({ route, setRoute, theme, onToggleTheme, region, setRegion }) {
  const [open, setOpen] = useState(false);
  const [focusReq, setFocusReq] = useState(0);
  const searchRef = useRef(null);
  const drawerSearchRef = useRef(null);

  // « Chercher un invocateur » (accueil) : focus de la recherche, ou ouverture
  // du menu tiroir quand la barre de recherche est masquée (mobile).
  useEffect(() => {
    const h = () => {
      const el = searchRef.current;
      if (el && el.offsetParent !== null) { el.focus(); }
      else { setOpen(true); setFocusReq((n) => n + 1); }
    };
    window.addEventListener('btr:focus-search', h);
    return () => window.removeEventListener('btr:focus-search', h);
  }, []);
  useEffect(() => {
    if (open && focusReq) { const t = setTimeout(() => drawerSearchRef.current?.focus(), 80); return () => clearTimeout(t); }
    return undefined;
  }, [open, focusReq]);
  // Le tiroir se ferme au changement d'écran, à Échap, et bloque le scroll de la page.
  useEffect(() => { setOpen(false); }, [route]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open]);

  const go = (id) => { setOpen(false); setRoute(id); };
  const search = (q) => {
    const t = (q || "").trim();
    setOpen(false);
    if (!t) return;
    const i = t.indexOf("#");
    if (i > 0) {
      setRoute("profile", { summoner: { region, name: t.slice(0, i).trim(), tag: t.slice(i + 1).trim() }, query: null });
    } else {
      // Pas de tag : on n'affiche pas un inconnu, on explique le format attendu.
      setRoute("profile", { summoner: null, query: t });
    }
  };

  const regionSelect = (extra) => (
    <select className="nav-region" value={region} onChange={(e) => setRegion(e.target.value)}
            title="Région de recherche" aria-label="Région" {...extra}>
      {REGION_CODES.map((r) => <option key={r} value={r}>{r}</option>)}
    </select>
  );

  return (
    <>
      <nav className="nav">
        <button className="nav-brand" onClick={() => go("home")} aria-label="Accueil BuildTheRift">
          <span className="nav-brand-mark">b<span style={{opacity:0.55}}>.</span></span>
          <span style={{display:"flex",flexDirection:"column",lineHeight:1}}>
            <span>BuildTheRift</span>
            <span className="nav-brand-sub">Patch {ddVersion()}</span>
          </span>
        </button>
        <div className="nav-tabs">
          {NAV_TABS.map(t => (
            <button key={t.id} className={"nav-tab " + (route === t.id ? "active" : "")} onClick={() => go(t.id)}>
              {t.label}
            </button>
          ))}
          {/* Module tournoi : vraie route navigateur (/tournament), pas un écran interne */}
          <Link to="/tournament" className="nav-tab">Tournois</Link>
        </div>
        <div className="nav-right">
          {regionSelect()}
          <NavSearch onSubmit={search} inputRef={searchRef}/>
          <button className="nav-avatar" onClick={onToggleTheme} title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'} aria-label="Changer de thème">
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={16}/>
          </button>
          <button className="nav-burger" onClick={() => setOpen(o => !o)} aria-label={open ? "Fermer le menu" : "Ouvrir le menu"} aria-expanded={open}>
            <Icon name={open ? "x" : "menu"} size={20}/>
          </button>
        </div>
      </nav>
      {open && (
        <div className="nav-drawer-backdrop" onClick={() => setOpen(false)}>
          <div className="nav-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Menu">
            <form className="nav-search drawer-search" role="search"
                  onSubmit={e => { e.preventDefault(); search(drawerSearchRef.current?.value); }}>
              <Icon name="search" size={15} style={{color:"var(--ink-3)"}}/>
              <input ref={drawerSearchRef} placeholder="Invocateur (Nom#TAG)…" aria-label="Rechercher un invocateur"/>
              {regionSelect({ className: "nav-region drawer-region" })}
            </form>
            <div className="drawer-tabs">
              {NAV_TABS.map(t => (
                <button key={t.id} className={"drawer-tab " + (route === t.id ? "active" : "")} onClick={() => go(t.id)}>{t.label}</button>
              ))}
              <Link to="/tournament" className="drawer-tab" onClick={() => setOpen(false)}>Tournois</Link>
              <button className={"drawer-tab " + (route === 'companion' ? "active" : "")} onClick={() => go("companion")}>Companion desktop</button>
            </div>
            <div className="drawer-foot">
              <button className="btn outline sm" onClick={onToggleTheme}>
                <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={14}/> {theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
              </button>
              <button className="btn ghost sm" onClick={() => setOpen(false)}><Icon name="x" size={14}/> Fermer</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function NavSearch({ onSubmit, inputRef }) {
  const [q, setQ] = useState("");
  return (
    <form className="nav-search" role="search" onSubmit={e => { e.preventDefault(); onSubmit(q); }}>
      <Icon name="search" size={15} style={{color: "var(--ink-3)"}}/>
      <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
             placeholder="Invocateur (Nom#TAG)…" aria-label="Rechercher un invocateur"/>
      <span className="kbd">⏎</span>
    </form>
  );
}

// ---------- Pied de page ----------
function SiteFooter({ setRoute }) {
  const links = [
    ["champions", "Champions"], ["build", "Builds"], ["profile", "Profil"], ["status", "Serveurs Riot"],
    ["guesser", "Jeux"], ["arena", "Arena · Challenge Top 1"], ["clash", "Clash · Scouting"],
  ];
  return (
    <footer className="site-footer">
      <div className="container" style={{paddingTop:36,paddingBottom:36}}>
        <div className="footer-grid">
          <div>
            <div className="nav-brand" style={{cursor:"default"}}>
              <span className="nav-brand-mark">b<span style={{opacity:0.55}}>.</span></span>
              <span>BuildTheRift</span>
            </div>
            <p className="muted" style={{fontSize:13,marginTop:12,maxWidth:380,lineHeight:1.5}}>
              Stats, builds, profils et outils communautaires pour League of Legends, calculés
              sur des millions de parties classées européennes.
            </p>
            <p className="muted" style={{fontSize:12,marginTop:10}}>Données : Riot Games API · Data Dragon patch {ddVersion()}</p>
          </div>
          <div>
            <div className="footer-title">Explorer</div>
            {links.map(([id, l]) => <button key={id} className="footer-link" onClick={() => setRoute(id)}>{l}</button>)}
            <Link to="/tournament" className="footer-link">Tournois Arena</Link>
          </div>
          <div>
            <div className="footer-title">Le projet</div>
            <button className="footer-link" onClick={() => setRoute("companion")}>Companion desktop</button>
            <button className="footer-link" onClick={() => setRoute("mentions")}>Mentions légales</button>
            <button className="footer-link" onClick={() => setRoute("confidentialite")}>Confidentialité</button>
            <a className="footer-link" href="mailto:contact@buildtherift.eu">Contact</a>
          </div>
        </div>
        <p className="footer-legal">
          BuildTheRift n'est pas approuvé par Riot Games et ne reflète pas les opinions de Riot Games
          ni de quiconque participe officiellement à la production ou à la gestion des propriétés de
          Riot Games. Riot Games et toutes les propriétés associées sont des marques commerciales ou
          des marques déposées de Riot Games, Inc. League of Legends © Riot Games, Inc.
        </p>
      </div>
    </footer>
  );
}

// ---------- Section Header ----------
function SecHead({ eyebrow, title, meta, action }) {
  return (
    <div className="sec-head">
      <div className="h-group">
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <h2>{title}</h2>
        {meta && <div className="muted" style={{fontSize: 13, marginTop: 2}}>{meta}</div>}
      </div>
      {action && <div className="actions">{action}</div>}
    </div>
  );
}

// ---------- ChampImg / ItemImg ----------
function ChampImg({ id, size = "md", className = "", hover, glow, round }) {
  const cls = ["champ", `sz-${size}`, className, hover && "hover", glow && "glow", round && "round"].filter(Boolean).join(" ");
  return (
    <div className={cls}>
      <img src={champSquare(id)} alt={id} loading="lazy" decoding="async"/>
    </div>
  );
}
function ItemImg({ id, size = 32 }) {
  if (!id || Number(id) <= 0) {
    return <div className="item empty" style={{width: size, height: size}}/>;
  }
  return (
    <div className="item" style={{width: size, height: size}}>
      <img src={ddItemImg(id)} alt="" loading="lazy"
           onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}/>
    </div>
  );
}
// Sort d'invocateur par id numérique Riot (données réelles) via ddragon.
function SpellById({ id, size = 24 }) {
  const sp = getSpell(id);
  if (!sp) return <div className="item empty" style={{width: size, height: size}}/>;
  return (
    <div className="item" style={{width: size, height: size}}>
      <img src={spellImgByKey(sp.key)} alt={sp.name} loading="lazy"/>
    </div>
  );
}
function ItemsRow({ ids, size = 30, gap = 4 }) {
  return (
    <div style={{display:"flex", gap}}>
      {ids.map((id, i) => <ItemImg key={i} id={id} size={size}/>)}
    </div>
  );
}

// ---------- KDA ----------
function KDA({ k, d, a, showRatio = true, big }) {
  const ratio = d === 0 ? "Parfait" : ((k + a) / d).toFixed(2);
  return (
    <span className="kda" style={big ? {fontSize:18} : null}>
      <span>{k}</span><span className="sep">/</span>
      <span style={{color:"var(--loss)"}}>{d}</span><span className="sep">/</span>
      <span>{a}</span>
      {showRatio && <span className="ratio">{ratio} {typeof ratio === "string" ? "" : "KDA"}</span>}
    </span>
  );
}

// ---------- Meter ----------
function Meter({ value, max = 100, kind }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="meter">
      <div className={`meter-fill ${kind || ""}`} style={{width: pct + "%"}}/>
    </div>
  );
}

// ---------- Form strip ----------
function FormStrip({ results }) {
  return (
    <div className="form-strip">
      {results.map((r, i) => (
        <span key={i} className={"f " + (r ? "win" : "loss")}>{r ? "V" : "D"}</span>
      ))}
    </div>
  );
}

// ---------- KPI ----------
function Kpi({ label, value, trend, trendDir, accent }) {
  return (
    <div className="kpi">
      <div className="lbl">{label}</div>
      <div className="val" style={accent ? {color:"var(--accent)"} : null}>{value}</div>
      {trend && (
        <div className={"trend " + (trendDir === "up" ? "up" : trendDir === "down" ? "down" : "")}>
          {trendDir === "up" && <Icon name="trendUp" size={12}/>}
          {trendDir === "down" && <Icon name="trendDown" size={12}/>}
          {trend}
        </div>
      )}
    </div>
  );
}

// ---------- BigNum ----------
function BigNum({ value, sub, color, size = 48 }) {
  return (
    <div>
      <div className="bignum" style={{fontSize: size, color: color || "var(--ink)"}}>{value}</div>
      {sub && <div className="muted" style={{marginTop:6, fontSize:13}}>{sub}</div>}
    </div>
  );
}

// ---------- LineChart ----------
function LineChart({ values, accent = "accent", height = 200, fill = true, startLabel, endLabel = "Aujourd'hui" }) {
  const w = 600, h = height, pad = 16, padR = 12, padT = 12, padB = 24;
  const min = Math.min(...values), max = Math.max(...values);
  const span = max - min || 1;
  const x = (i) => pad + (i * (w - pad - padR)) / (values.length - 1);
  const y = (v) => h - padB - ((v - min) / span) * (h - padT - padB);
  const path = values.map((v, i) => (i === 0 ? "M" : "L") + x(i) + " " + y(v)).join(" ");
  const area = path + ` L${x(values.length-1)} ${h-padB} L${x(0)} ${h-padB} Z`;
  const colorVar = accent === "win" ? "var(--win)" : accent === "loss" ? "var(--loss)" : "var(--accent)";
  const id = useMemo(() => "g" + Math.random().toString(36).slice(2,7), []);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{width:"100%",height,display:"block"}}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={colorVar} stopOpacity="0.30"/>
          <stop offset="100%" stopColor={colorVar} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <g className="chart-grid">
        {[0,1,2,3].map(i => <line key={i} x1={pad} x2={w-padR} y1={padT + i*(h-padT-padB)/3} y2={padT + i*(h-padT-padB)/3}/>)}
      </g>
      {fill && <path d={area} fill={`url(#${id})`}/>}
      <path d={path} fill="none" stroke={colorVar} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"/>
      {values.map((v, i) => (
        <circle key={i} cx={x(i)} cy={y(v)} r="3.5" fill="var(--bg-2)" stroke={colorVar} strokeWidth="2"/>
      ))}
      <g className="chart-axis">
        <text x={pad} y={h-6}>{startLabel ?? `J-${values.length-1}`}</text>
        <text x={w-padR} y={h-6} textAnchor="end">{endLabel}</text>
      </g>
    </svg>
  );
}

// ---------- Sparkline ----------
function Sparkline({ values, color, width = 80, height = 24 }) {
  const min = Math.min(...values), max = Math.max(...values);
  const span = max - min || 1;
  const x = (i) => (i * width) / (values.length - 1);
  const y = (v) => height - ((v - min) / span) * (height - 4) - 2;
  const path = values.map((v, i) => (i === 0 ? "M" : "L") + x(i) + " " + y(v)).join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{display:"block"}}>
      <path d={path} fill="none" stroke={color || "var(--accent)"} strokeWidth="1.6" strokeLinejoin="round"/>
    </svg>
  );
}

// ---------- Rank Crest ----------
function RankCrest({ tier, division, size = 60 }) {
  const tierKey = (tier || "").toLowerCase()
    .replace(/[éèê]/g, "e").replace(/[à]/g, "a").replace(/[î]/g, "i").replace(/[ô]/g, "o");
  const colors = {
    fer: ["#3A3032", "#71686A"],
    bronze: ["#5C3520", "#A06A40"],
    argent: ["#8090A0", "#C0C8D0"],
    or: ["#A77E2C", "#F0C870"],
    platine: ["#2A857F", "#5BDEE0"],
    emeraude: ["#1E7D40", "#4ADE80"],
    diamant: ["#2E4D8E", "#88AEEA"],
    maitre: ["#5A2870", "#C36AE0"],
    challenger: ["#0E5A7E", "#3FB8E8"],
  };
  const [dark, light] = colors[tierKey] || ["#888", "#bbb"];
  const id = "g-" + tierKey;
  return (
    <svg width={size} height={size} viewBox="0 0 60 60" style={{flexShrink:0,filter:"drop-shadow(0 4px 12px rgba(0,0,0,0.4))"}}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={light}/>
          <stop offset="100%" stopColor={dark}/>
        </linearGradient>
      </defs>
      <polygon points="30,4 52,16 52,40 30,56 8,40 8,16"
               fill={`url(#${id})`}/>
      <polygon points="30,4 52,16 52,40 30,56 8,40 8,16"
               fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1"/>
      <text x="30" y="36" textAnchor="middle"
            style={{fontFamily:"var(--font-display)",fontSize:14,fontWeight:800,fill:"#fff",letterSpacing:"-0.04em"}}>
        {division || ""}
      </text>
    </svg>
  );
}

// ---------- Champion selector ----------
function ChampSelector({ value, onChange, placeholder = "Choisir un champion" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, []);
  const champs = allChamps();
  const champ = champs.find(c => c.id === value);
  const filtered = champs.filter(c => matchesQuery(c.name, q) || matchesQuery(c.id, q))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr')).slice(0, 80);
  return (
    <div ref={ref} style={{position:"relative",width:"100%"}}>
      <button className="field" onClick={() => setOpen(!open)} style={{width:"100%",gap:10}}>
        {champ ? (
          <>
            <ChampImg id={champ.id} size="xs"/>
            <span style={{flex:1,textAlign:"left",fontWeight:600}}>{champ.name}</span>
            <span style={{color:"var(--ink-3)",fontSize:12,fontStyle:"italic"}}>{champ.title}</span>
          </>
        ) : (
          <span style={{flex:1,textAlign:"left",color:"var(--ink-mute)"}}>{placeholder}</span>
        )}
        <Icon name="chevD" size={14}/>
      </button>
      {open && (
        <div style={{
          position:"absolute",top:"calc(100% + 6px)",left:0,right:0,
          background:"var(--surface)",border:"1px solid var(--border-2)",
          maxHeight:380,overflowY:"auto",zIndex:30,
          borderRadius:14, boxShadow:"0 12px 32px rgba(0,0,0,0.4)",
        }}>
          <div style={{padding:8,borderBottom:"1px solid var(--border)",position:"sticky",top:0,background:"var(--surface)"}}>
            <div className="field" style={{width:"100%",padding:"6px 12px"}}>
              <Icon name="search" size={14} style={{color:"var(--ink-3)"}}/>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filtrer..." autoFocus/>
            </div>
          </div>
          {filtered.map(c => (
            <button key={c.id}
                    onClick={() => { onChange(c.id); setOpen(false); setQ(""); }}
                    style={{
                      display:"flex",alignItems:"center",gap:10,width:"100%",
                      padding:"8px 12px",
                      textAlign:"left",fontSize:13,
                      transition:"background .12s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--surface-2)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <ChampImg id={c.id} size="xs"/>
              <span style={{fontWeight:600}}>{c.name}</span>
              <span style={{flex:1,color:"var(--ink-3)",fontSize:11,fontStyle:"italic",marginLeft:6}}>{c.title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Stat strip ----------
function StatStrip({ value, label, sub, color }) {
  return (
    <div>
      <div className="bignum" style={{fontSize: 32, color: color || "var(--ink)"}}>{value}</div>
      <div style={{fontSize: 12, color: "var(--ink-3)", marginTop: 4}}>
        <strong style={{color:"var(--ink-2)", fontWeight: 600}}>{label}</strong>
        {sub && <span style={{marginLeft:6}}>{sub}</span>}
      </div>
    </div>
  );
}

// ---------- États (chargement / erreur / vide) ----------
function Spinner({ label = "Chargement…" }) {
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:12,padding:"48px 0",color:"var(--ink-3)"}}>
      <span className="btr-spin" style={{
        width:18,height:18,borderRadius:"50%",
        border:"2px solid var(--surface-3)",borderTopColor:"var(--accent)",
        display:"inline-block",
      }}/>
      <span style={{fontSize:14}}>{label}</span>
      <style>{`@keyframes btrspin{to{transform:rotate(360deg)}} .btr-spin{animation:btrspin .7s linear infinite}`}</style>
    </div>
  );
}
function ErrorState({ error, onRetry, label = "Impossible de charger les données" }) {
  return (
    <div className="card" style={{textAlign:"center",padding:"36px 24px"}}>
      <div style={{color:"var(--loss)",fontWeight:700,marginBottom:6}}>{label}</div>
      {error && <div className="muted" style={{fontSize:13,marginBottom:14}}>{String(error.message || error)}</div>}
      {onRetry && <button className="btn outline" onClick={onRetry}><Icon name="refresh" size={14}/> Réessayer</button>}
    </div>
  );
}
function EmptyState({ icon = "search", title = "Aucune donnée", hint }) {
  return (
    <div className="card" style={{textAlign:"center",padding:"40px 24px"}}>
      <div style={{margin:"0 auto 14px",width:48,height:48,borderRadius:14,background:"var(--surface-2)",
                   display:"flex",alignItems:"center",justifyContent:"center",color:"var(--ink-3)"}}>
        <Icon name={icon} size={20}/>
      </div>
      <div className="h4">{title}</div>
      {hint && <p className="muted" style={{fontSize:13,marginTop:6,marginBottom:0}}>{hint}</p>}
    </div>
  );
}
// `state` = { loading, error, data } d'useApi ; affiche le bon fallback ou les enfants.
function AsyncBlock({ state, onRetry, children, emptyWhen, empty }) {
  if (state.loading && !state.data) return <Spinner/>;
  if (state.error && !state.data) return <ErrorState error={state.error} onRetry={onRetry}/>;
  if (emptyWhen && emptyWhen(state.data)) return empty || <EmptyState/>;
  return children;
}

// ---------- Export ----------
export {
  Icon, ICON, BTR, NavBar, NavSearch, SiteFooter, NAV_TABS, SecHead,
  ChampImg, ItemImg, SpellById, ItemsRow,
  KDA, Meter, FormStrip,
  Kpi, BigNum, LineChart, Sparkline,
  RankCrest, ChampSelector, StatStrip,
  Spinner, ErrorState, EmptyState, AsyncBlock,
};
